<template>
  <v-container fluid>
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="orderSettings"
        :options.sync="options"
        item-key="country_code"
        :footer-props="footerProps"
        :loading-text="$t('order.settings.settingsLoading')"
        :no-data-text="$t('order.settings.noSettings')"
        mobile-breakpoint="481"
        :loading="loadingData"
        dense
      >
        <template v-slot:top>
          <v-toolbar flat class="my-1">
            <v-row v-if="$vuetify.breakpoint.width >= 520" no-gutters align="center">
              <v-col cols="9" md="7" xl="6" class="pr-2">
                <countries-autocomplete
                  v-model="selectedCountries"
                  :label="$t('settings.country')"
                  clearable
                  dense
                  hide-details
                  outlined
                  multiple
                  required
                  @blur="reloadList"
                  @click:clear="reloadList"
                ></countries-autocomplete>
              </v-col>
              <v-col cols="auto">
                <v-btn @click="createSetting" color="primary" outlined><v-icon>mdi-plus</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-toolbar v-if="$vuetify.breakpoint.width < 520" flat class="my-1">
            <countries-autocomplete
              v-model="selectedCountries"
              :label="$t('settings.country')"
              clearable
              dense
              hide-details
              outlined
              multiple
              required
              class="mr-2 flex-fill-min"
              @blur="reloadList"
              @click:clear="reloadList"
            ></countries-autocomplete>
            <v-btn @click="createSetting" color="primary" outlined><v-icon>mdi-plus</v-icon></v-btn>
          </v-toolbar>
        </template>
        <template v-slot:item.country_code="{ item }">
          {{ item.country_code }}
        </template>
        <template v-slot:item.updated_at="{ item }">
          {{ formatDate(item.updated_at) }}
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn
            :class="[$vuetify.breakpoint.width >= 481 ? '' : 'pr-0']"
            text
            color="primary"
            @click="executeAction(item.country_code)"
          >
            {{ $t('return.settings.details') }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    <simple-choice-dialog
      v-model="formSettingsDialog"
      :title="$t('settings.selectCountry')"
      :introductionSentence="$t('settings.selectACountryBelow')"
      :choices="countriesWithNoSettings"
      :choicesListLabel="$t('settings.country')"
      @confirm="executeAction"
    ></simple-choice-dialog>
  </v-container>
</template>

<script>
  import { EventBus } from '@/event-bus'
  import { mapState } from 'vuex'
  import moment from 'moment'
  import SimpleChoiceDialog from '../../components/dialogs/SimpleChoiceDialog.vue'
  import CountriesAutocomplete from '../../components/CountriesAutocomplete.vue'

  export default {
    components: { SimpleChoiceDialog, CountriesAutocomplete },
    name: 'OrdersSettingsTable',
    data() {
      return {
        orderSettings: [],
        formFiltersKey: Date.now(),
        footerProps: {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('settings.configurationsPerPage'),
        },
        options: {
          itemsPerPage: 15,
        },
        loadingData: true,
        totalOrdersSettings: 0,
        formSettingsDialog: false,
        settingToUpdate: null,
        countries: [],
        selectedCountries: [],
        countriesWithSettingsLoaded: false,
        countriesWithSettings: [],
      }
    },
    mounted() {
      this.countries = this.$loadCountries(this.$i18n.locale)
    },
    activated() {
      let previousPath = this.$store.state.previousPath.split('/')[1]
      // we reset filters if the previous page wasn't the details of a seller order OR if vue recycled it from a different instance
      if (previousPath !== 'orders-settings') {
        this.options.itemsPerPage = 15
        this.options.page = 1
        this.options.sortBy = []
        this.options.sortDesc = []
        this.selectedCountries = []
      }
      this.orderSettings = []
      this.list()
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService', 'roleRights']),

      status() {
        return [
          { text: this.$t('return.settings.enabled'), value: true },
          { text: this.$t('return.settings.disabled'), value: false },
        ]
      },
      headers() {
        return [
          {
            text: this.$t('return.settings.countryCode'),
            value: 'country_code',
            filterType: 'text',
            sortable: true,
          },
          {
            text: this.$t('settings.lastUpdate'),
            value: 'updated_at',
            filterType: 'text',
            sortable: true,
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ]
      },
      countriesWithNoSettings() {
        if (this.countriesWithSettings.length && this.countries.length) {
          return this.countries
            .filter((country) => !this.countriesWithSettings.includes(country.iso_3_code))
            .map((country) => {
              return {
                display: `(${country.iso_3_code})  ${country.name}`,
                value: country.iso_3_code,
              }
            })
        } else {
          return this.countries.map((country) => {
            return {
              display: `(${country.iso_3_code})  ${country.name}`,
              value: country.iso_3_code,
            }
          })
        }
      },
    },
    methods: {
      executeAction: function (countryCode) {
        this.$router.push({ path: `/orders-settings/${countryCode}` })
      },
      async reloadList() {
        await this.$auth.isAuthenticated()
        this.list()
      },
      list() {
        this.loadingData = true
        let { page, itemsPerPage, sortDesc, sortBy } = this.options
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            sortBy: sortBy.length === 0 ? 'date' : sortBy,
            sortDesc: sortDesc.length === 0 ? true : sortDesc,
            page: page,
            itemsPerPage: itemsPerPage,
          },
        }
        if (this.selectedCountries.length > 0) {
          myInit.queryStringParameters.countries = this.selectedCountries
        }
        this.$Amplify.API.get('settings', '/v1/settings', myInit)
          .then((res) => {
            this.orderSettings = res.settings
            this.totalOrdersSettings = res.nbOrdersSettings
            this.loadingData = false
            if (this.totalOrdersSettings < (this.options.page - 1) * this.options.itemsPerPage + 1) {
              let pageNumber = Math.floor(this.totalOrdersSettings / this.options.itemsPerPage)
              this.options.page = pageNumber === 0 ? 1 : pageNumber
            }
            if (!this.countriesWithSettingsLoaded) {
              for (const settings of this.orderSettings) {
                this.countriesWithSettings.push(settings.country_code)
              }
              this.countriesWithSettingsLoaded = true
            }
          })
          .catch((e) => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e.message,
              color: 'error',
            })
            this.loadingData = false
          })
      },
      createSetting() {
        this.settingToUpdate = null
        this.formSettingsDialog = true
      },
      close() {
        this.formSettingsDialog = false
      },
      formatDate(date) {
        return date ? moment(date).format('DD/MM/YY HH:mm') : ''
      },
    },
  }
</script>

<style scoped>
  .flex-fill-min {
    flex: 1;
    min-width: 0;
  }
</style>
